import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { usd } from "@/lib/constants";
import { DISTRESS_TYPES, AUCTION_STATUSES, VERIFICATION_STATUSES, VERIF_LABELS, VERIF_BADGE, DATE_OPS, PROSPECT_LEAD_STATUSES } from "@/lib/prospectConstants";
import { TextField, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ProspectDetail from "@/pages/prospecting/ProspectDetail";
import SavedSearches from "@/pages/prospecting/SavedSearches";
import UsageSummary from "@/pages/prospecting/UsageSummary";
import { Search, MapPin, Star, Plus, Calculator, SlidersHorizontal, ChevronDown, RefreshCw, Home, Database } from "lucide-react";

const EMPTY_FILTERS = {
  county: "", city: "", state: "", zip_code: "", address: "", apn_case: "", distress_type: "",
  auction_status: "", foreclosure_status: "", owner_name: "", owner_mailing: "", phone: "",
  min_value: "", max_value: "", min_equity: "", max_equity: "",
  date_field: "auction", date_op: "", date_from: "", date_to: "",
  verification_status: "", lead_status: "", starred: false, added_from: "", added_to: "",
};

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || fallback;
  return d || fallback;
}

export function VerifBadge({ status }) {
  const s = status || "unverified";
  return <Badge variant="outline" className={`${VERIF_BADGE[s] || VERIF_BADGE.unverified} text-[10px]`} data-testid="verif-badge">{VERIF_LABELS[s] || s}</Badge>;
}

function ExactLookup({ onResult, nav }) {
  const [a, setA] = useState({ street_address: "", unit: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState("");
  const [areaRes, setAreaRes] = useState(null);
  const [rowBusy, setRowBusy] = useState("");
  const [contacts, setContacts] = useState({});
  const set = (k) => (v) => setA((p) => ({ ...p, [k]: v }));
  const zipMode = !a.street_address.trim() && !!a.zip.trim();

  const run = async (refresh) => {
    if (zipMode) return zipSearch();
    if (!a.street_address.trim()) return toast.error("Enter a street address — or just a ZIP for an area search");
    setBusy(refresh ? "refresh" : "search");
    try {
      const { data } = await api.post("/prospects/exact-lookup", { ...a, refresh });
      if (!data.found) toast.message(data.note || "No property record found for that address.");
      else if (data.already_exists) toast.message("Property already exists — showing the existing record.");
      else toast.success(data.api_call_made ? "Property data retrieved from provider" : "Property loaded from local data");
      onResult(data);
    } catch (e) { toast.error(errMsg(e, "Lookup failed")); }
    finally { setBusy(""); }
  };

  const zipSearch = async () => {
    setBusy("search"); setAreaRes(null);
    try {
      const { data } = await api.get("/prospecting/search", { params: { postalcode: a.zip.trim() } });
      setAreaRes(data.results || []);
      if (!(data.results || []).length) toast.message("No properties returned for that ZIP.");
    } catch (e) { toast.error(errMsg(e, "ZIP area search failed")); }
    finally { setBusy(""); }
  };

  const saveOrGetProspect = async (row) => {
    try {
      const { data } = await api.post("/prospects", {
        property_address: row.property_address, city: row.city, state: row.state, zip_code: row.zip_code,
        county: row.county, apn: row.parcel_number, property_type: row.property_type,
        owner_name: row.owner_name, estimated_value: row.estimated_market_value,
        estimated_mortgage_balance: row.estimated_loan_balance, estimated_equity: row.estimated_equity,
        beds: row.bedrooms, baths: row.bathrooms, living_sqft: row.square_feet, year_built: row.year_built,
        distress_type: row.absentee ? "Absentee Owner" : "", source: "ATTOM",
      });
      return { id: data.id, existed: false };
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.existing_id) return { id: d.existing_id, existed: true };
      throw err;
    }
  };

  const rowSave = async (row) => {
    setRowBusy(row.attom_id + ":s");
    try {
      const { existed } = await saveOrGetProspect(row);
      toast[existed ? "message" : "success"](existed ? "Property already exists in your prospect database" : "Prospect saved to database");
      onResult({ found: true });
    } catch (e) { toast.error(errMsg(e, "Could not save prospect")); }
    finally { setRowBusy(""); }
  };

  const rowLead = async (row) => {
    setRowBusy(row.attom_id + ":l");
    try {
      const { id } = await saveOrGetProspect(row);
      const { data } = await api.post(`/prospects/${id}/to-lead`);
      toast.success(data.existing ? "Opening existing lead" : "Lead created");
      nav(`/leads/${data.lead_id}`);
    } catch (e) { toast.error(errMsg(e, "Could not create lead")); }
    finally { setRowBusy(""); }
  };

  const rowTrace = async (row) => {
    setRowBusy(row.attom_id + ":t");
    try {
      const { data } = await api.post("/prospecting/skip-trace", {
        owner_name: row.owner_name, property_address: row.property_address,
        city: row.city, state: row.state, zip_code: row.zip_code });
      setContacts((c) => ({ ...c, [row.attom_id]: data }));
      if (data.mocked) toast.message("Demo skip-trace — connect a provider for real contacts");
      else if (data.no_match) toast.message("No contact information found.");
      else toast.success("Skip trace completed.");
    } catch (e) { toast.error(errMsg(e, "Skip trace could not be completed.")); }
    finally { setRowBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-md p-4 sm:p-5" data-testid="exact-lookup-panel">
      <div className="flex items-center gap-2 mb-3"><Home className="w-4 h-4 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Property Lookup — Exact Address or ZIP Area Search</span></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2"><TextField label="Street Address" value={a.street_address} onChange={set("street_address")} testid="lookup-street" placeholder="123 Main St (leave blank for ZIP search)" /></div>
        <TextField label="Unit" value={a.unit} onChange={set("unit")} testid="lookup-unit" />
        <TextField label="City" value={a.city} onChange={set("city")} testid="lookup-city" />
        <TextField label="State" value={a.state} onChange={set("state")} testid="lookup-state" placeholder="NV" />
        <TextField label="ZIP" value={a.zip} onChange={set("zip")} testid="lookup-zip" />
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" onClick={() => run(false)} disabled={!!busy} data-testid="lookup-search-btn">
          <Search className="w-3.5 h-3.5 mr-1.5" />{busy === "search" ? "Searching…" : zipMode ? "Search ZIP Area" : "Search Property"}</Button>
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={!!busy || zipMode} data-testid="lookup-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{busy === "refresh" ? "Refreshing…" : "Refresh Property Data"}</Button>
        <span className="text-[11px] text-muted-foreground self-center">Full address = exact match (local database first, never forces a paid call). ZIP only = area search via ATTOM.</span>
      </div>
      {areaRes && (
        <div className="mt-4 border border-border rounded-md overflow-x-auto" data-testid="zip-area-results">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Address</TableHead><TableHead className="hidden md:table-cell">Owner</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Est. Value</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Est. Equity</TableHead>
              <TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {areaRes.map((row) => {
                const ct = contacts[row.attom_id];
                return (
                  <TableRow key={row.attom_id} data-testid={`zip-row-${row.attom_id}`}>
                    <TableCell className="font-medium">{row.property_address}
                      <span className="block text-xs font-normal text-muted-foreground">{row.city}, {row.state} {row.zip_code}</span></TableCell>
                    <TableCell className="hidden md:table-cell">{row.owner_name || "—"}
                      {ct && (ct.phones?.length || ct.emails?.length) ? (
                        <span className="block text-xs text-accent">{(ct.phones || [])[0] || ""} {(ct.emails || [])[0] || ""}</span>
                      ) : null}</TableCell>
                    <TableCell className="hidden sm:table-cell text-right font-mono-nums">{row.estimated_market_value != null ? usd(row.estimated_market_value) : "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-right font-mono-nums text-accent">{row.estimated_equity != null ? usd(row.estimated_equity) : "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" disabled={rowBusy === row.attom_id + ":s"} onClick={() => rowSave(row)} data-testid={`zip-save-${row.attom_id}`} title="Save to Prospect Database">
                        <Star className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" disabled={rowBusy === row.attom_id + ":t"} onClick={() => rowTrace(row)} data-testid={`zip-trace-${row.attom_id}`} title="Skip Trace">
                        {rowBusy === row.attom_id + ":t" ? "Tracing…" : "Skip Trace"}</Button>
                      <Button size="sm" variant="outline" disabled={rowBusy === row.attom_id + ":l"} onClick={() => rowLead(row)} data-testid={`zip-lead-${row.attom_id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" />Add to Leads</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function FilterPanel({ f, setF, onApply, onClear }) {
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3" data-testid="prospect-filter-panel">
      <TextField label="County" value={f.county} onChange={set("county")} testid="pf-county" />
      <TextField label="City" value={f.city} onChange={set("city")} testid="pf-city" />
      <TextField label="State" value={f.state} onChange={set("state")} testid="pf-state" placeholder="NV" />
      <TextField label="ZIP" value={f.zip_code} onChange={set("zip_code")} testid="pf-zip" />
      <TextField label="Property Address" value={f.address} onChange={set("address")} testid="pf-address" />
      <TextField label="APN / Case Number" value={f.apn_case} onChange={set("apn_case")} testid="pf-apn-case" />
      <SelectField label="Distress Type" value={f.distress_type} onChange={set("distress_type")} options={DISTRESS_TYPES} testid="pf-distress" />
      <SelectField label="Auction / Status" value={f.auction_status} onChange={set("auction_status")} options={AUCTION_STATUSES} testid="pf-auction-status" />
      <TextField label="Owner Name (partial)" value={f.owner_name} onChange={set("owner_name")} testid="pf-owner" />
      <TextField label="Owner Mailing Address" value={f.owner_mailing} onChange={set("owner_mailing")} testid="pf-mailing" />
      <TextField label="Public Phone" value={f.phone} onChange={set("phone")} testid="pf-phone" />
      <SelectField label="Verification / Source" value={VERIF_LABELS[f.verification_status] || ""}
        onChange={(lbl) => set("verification_status")(VERIFICATION_STATUSES.find(([, l]) => l === lbl)?.[0] || "")}
        options={VERIFICATION_STATUSES.map(([, l]) => l)} testid="pf-verification" />
      <TextField label="Est. Value — Min ($)" type="number" value={f.min_value} onChange={set("min_value")} testid="pf-min-value" />
      <TextField label="Est. Value — Max ($)" type="number" value={f.max_value} onChange={set("max_value")} testid="pf-max-value" />
      <TextField label="Est. Equity — Min ($)" type="number" value={f.min_equity} onChange={set("min_equity")} testid="pf-min-equity" />
      <TextField label="Est. Equity — Max ($)" type="number" value={f.max_equity} onChange={set("max_equity")} testid="pf-max-equity" />
      <SelectField label="Date Field" value={f.date_field === "default" ? "Default Date" : "Auction Date"}
        onChange={(v) => set("date_field")(v === "Default Date" ? "default" : "auction")} options={["Auction Date", "Default Date"]} testid="pf-date-field" />
      <SelectField label="Date Condition" value={DATE_OPS.find(([v]) => v === f.date_op)?.[1] || ""}
        onChange={(lbl) => set("date_op")(DATE_OPS.find(([, l]) => l === lbl)?.[0] || "")} options={DATE_OPS.map(([, l]) => l)} testid="pf-date-op" />
      <TextField label={["between"].includes(f.date_op) ? "Date From" : "Date"} type="date" value={f.date_from} onChange={set("date_from")} testid="pf-date-from" />
      {f.date_op === "between" && <TextField label="Date To" type="date" value={f.date_to} onChange={set("date_to")} testid="pf-date-to" />}
      <SelectField label="Lead Status" value={f.lead_status} onChange={set("lead_status")} options={PROSPECT_LEAD_STATUSES} testid="pf-lead-status" />
      <TextField label="Date Added — From" type="date" value={f.added_from} onChange={set("added_from")} testid="pf-added-from" />
      <TextField label="Date Added — To" type="date" value={f.added_to} onChange={set("added_to")} testid="pf-added-to" />
      <label className="flex items-end gap-2 pb-2 cursor-pointer select-none">
        <Checkbox checked={f.starred} onCheckedChange={(v) => set("starred")(!!v)} data-testid="pf-starred" />
        <span className="text-sm">Saved / Starred only</span>
      </label>
      <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
        <Button size="sm" onClick={onApply} data-testid="pf-apply"><Search className="w-3.5 h-3.5 mr-1.5" />Apply Filters</Button>
        <Button size="sm" variant="ghost" onClick={onClear} data-testid="pf-clear">Clear All</Button>
      </div>
    </div>
  );
}

export default function ProspectDatabase() {
  const [q, setQ] = useState("");
  const [f, setF] = useState(EMPTY_FILTERS);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [busyRow, setBusyRow] = useState("");
  const [detail, setDetail] = useState(null);
  const [svEnabled, setSvEnabled] = useState(false);
  const debounceRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/streetview/status").then((r) => setSvEnabled(!!r.data.enabled)).catch(() => {}); }, []);

  const buildParams = useCallback((pg, fo, qo) => {
    const params = { page: pg, page_size: 25 };
    const qq = (qo !== undefined ? qo : q).trim();
    if (qq) params.q = qq;
    Object.entries(fo || f).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      params[k] = v;
    });
    return params;
  }, [q, f]);

  const load = useCallback(async (pg, fo, qo) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/prospects", { params: buildParams(pg, fo, qo) });
      setData(d); setPage(pg);
    } catch (e) { toast.error(errMsg(e, "Could not load prospects")); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1), 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const toggleStar = async (p) => {
    try {
      const { data: upd } = await api.patch(`/prospects/${p.id}`, { starred: !p.starred });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === p.id ? upd : x)) }));
    } catch (e) { toast.error(errMsg(e, "Could not update")); }
  };

  const toLead = async (p, analyze) => {
    setBusyRow(p.id + (analyze ? ":a" : ":l"));
    try {
      const { data: r } = await api.post(`/prospects/${p.id}/to-lead`);
      toast.success(r.existing ? "Opening existing lead" : "Lead created from prospect");
      nav(analyze ? `/analyzer?lead_id=${r.lead_id}` : `/leads/${r.lead_id}`);
    } catch (e) { toast.error(errMsg(e, "Could not create lead")); }
    finally { setBusyRow(""); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 25)));

  return (
    <div className="space-y-4" data-testid="prospect-database">
      <div className="bg-card border border-border rounded-md p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-2"><Database className="w-4 h-4 text-accent" />
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Search Property / Prospect</span></div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
            placeholder="Address, APN, case number, owner name, or phone — e.g. 123 Main Street, Las Vegas, NV 89101"
            data-testid="universal-search-input" />
        </div>
        {data.search_type && <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="search-type-hint">Searching by: <span className="font-medium capitalize">{data.search_type === "apn" ? "APN" : data.search_type}</span> (auto-detected)</p>}
        <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="mt-2 -ml-2 text-muted-foreground" data-testid="filters-toggle">
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />Filters<ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <FilterPanel f={f} setF={setF} onApply={() => load(1)} onClear={() => { setF(EMPTY_FILTERS); load(1, EMPTY_FILTERS); }} />
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="bg-card border border-border rounded-md p-4 sm:p-5">
        <SavedSearches currentFilters={f} currentQ={q} onRun={(filters) => {
          const { q: fq = "", ...rest } = filters || {};
          const nf = { ...EMPTY_FILTERS, ...rest };
          setQ(fq); setF(nf); setFiltersOpen(true);
          load(1, nf, fq);
        }} />
      </div>

      <ExactLookup nav={nav} onResult={(r) => {
        if (r.found && r.prospect) {
          if (r.already_exists) toast.message("PROPERTY ALREADY EXISTS", { description: "Opening the existing property record." });
          setDetail(r.prospect); load(1);
        }
      }} />

      <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid="prospect-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead className="w-8"></TableHead>
              <TableHead>Address</TableHead>
              <TableHead className="hidden md:table-cell">City / County</TableHead>
              <TableHead className="hidden lg:table-cell">APN</TableHead>
              <TableHead className="hidden md:table-cell">Owner</TableHead>
              <TableHead className="hidden sm:table-cell">Distress</TableHead>
              <TableHead className="hidden xl:table-cell">Status</TableHead>
              <TableHead className="hidden lg:table-cell">Auction</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Est. Value</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Est. Equity</TableHead>
              <TableHead className="hidden md:table-cell">Verification</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((p) => (
              <TableRow key={p.id} data-testid={`prospect-db-row-${p.id}`}>
                <TableCell>
                  <button onClick={() => toggleStar(p)} data-testid={`star-${p.id}`} title={p.starred ? "Unstar" : "Save / star"}>
                    <Star className={`w-4 h-4 ${p.starred ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40 hover:text-amber-400"}`} />
                  </button>
                </TableCell>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />{p.property_address}{p.unit ? ` ${p.unit}` : ""}</span>
                  <span className="block text-xs font-normal text-muted-foreground md:hidden">{p.city}, {p.state} {p.zip_code}</span>
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{p.city}{p.county ? ` · ${p.county}` : ""}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono-nums text-xs">{p.apn || "—"}</TableCell>
                <TableCell className="hidden md:table-cell">{p.owner_name || "—"}</TableCell>
                <TableCell className="hidden sm:table-cell">{p.distress_type ? <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-200 text-[10px]">{p.distress_type}</Badge> : "—"}</TableCell>
                <TableCell className="hidden xl:table-cell text-xs text-muted-foreground">{p.auction_status || p.foreclosure_status || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono-nums text-xs">{p.auction_date || "—"}</TableCell>
                <TableCell className="hidden sm:table-cell text-right font-mono-nums">{p.estimated_value != null ? usd(p.estimated_value) : "—"}</TableCell>
                <TableCell className="hidden sm:table-cell text-right font-mono-nums text-accent">{p.estimated_equity != null ? usd(p.estimated_equity) : "—"}</TableCell>
                <TableCell className="hidden md:table-cell"><VerifBadge status={p.verification_status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(p)} data-testid={`view-${p.id}`}>View</Button>                  {p.lead_id ? (
                    <Button size="sm" variant="ghost" onClick={() => nav(`/leads/${p.lead_id}`)} data-testid={`open-lead-${p.id}`}>Open Lead</Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled={busyRow === p.id + ":l"} onClick={() => toLead(p, false)} data-testid={`to-lead-${p.id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" />{busyRow === p.id + ":l" ? "Adding…" : "Add to Leads"}</Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={busyRow === p.id + ":a"} onClick={() => toLead(p, true)} data-testid={`analyze-${p.id}`} title="Send to Deal Analyzer">
                    <Calculator className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-10" data-testid="prospect-db-empty">
                No prospects yet. Use Exact Address Lookup above, save properties from Area Search, or adjust your filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="prospect-pagination">
        <span>{data.total} prospect{data.total === 1 ? "" : "s"} · page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => load(page - 1)} data-testid="page-prev">Previous</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => load(page + 1)} data-testid="page-next">Next</Button>
        </div>
      </div>

      <UsageSummary />

      <ProspectDetail prospect={detail} open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }} svEnabled={svEnabled}
        onUpdated={(upd) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === upd.id ? upd : x)) }))} />
    </div>
  );
}
